import React, { useCallback, useId, useMemo, useRef, useState } from 'react'
import { Link } from 'react-router-dom'
import { lmsAdmin } from '../api'
import { LmsIcon } from '../lms-icon'
import { FAILED_STATE, LmsGrid } from '../lms-grid'
import { lmsApiKeysVisible } from '../lms-admin-gate'
import { useRoleOrg } from '../../../role/hook/role-org'
import { ROLE_ORG } from '../../../role/role.context'
import { useGridList } from '../../../shared/sso-workspace/fleet-grid'
import { Notice } from '../../../shared/sso-workspace/fleet-page'
import { TableAgClient } from '../../../shared/table-builder/table-ag-client'
import { ROW_HEIGHT_TWO_LINE } from '../../../shared/table-builder/table-ag'
import { ColumnFormat, ColumnMenu, ColumnText } from '../../../shared/table-builder/table-columns'
import { IconAdd, IconPassword } from '../../../shared/icon/icon'
import { serverMessage } from '../../../shared/error/server-message'
import { ErrorReference } from '../../../shared/error/error-reference'
import { useErrorLine } from '../../../shared/error/use-error-line'
import { listKeys } from '../../../layout/authorized/org/api-keys/api'
import { useKeyWrites } from '../../../layout/authorized/org/api-keys/key-access'
import { ACCESS_OFF_BANNER, BOUND_EXPIRY, LMS_CONNECTION_BINDING, boundTo } from '../../../layout/authorized/org/api-keys/key-words'
import { useApiAccess } from '../../../layout/authorized/org/api-keys/use-api-access'
import { useKeyActions } from '../../../layout/authorized/org/api-keys/page/api-key-actions'
import { ApiKeyCreateDrawer } from '../../../layout/authorized/org/api-keys/page/api-key-create-drawer'
import { byField, createdCell, expiresCell, keyRow, lastUsedCell, nameCell, statusCell, useKeyMenu } from '../../../layout/authorized/org/api-keys/page/api-key-list'

// The connection drawer's "API keys for this connection" (Phase D D7,
// addendum D7-8 and Task 20.11): core API keys bound to this connection, each
// with an LMS API role, read-only or read-write (never admin: a bound key
// does no organization-wide LMS operation). The organization's own LMS keys
// are retired; a key for every connection is made on /org/api-keys with an
// LMS API role.
//
// - List: content's GET /api/ddd/api-key?binding_kind=lms_connection&
//   binding_id=<id> (API_KEY read), in the organization API keys page's
//   columns; each key's name opens its own page (/org/api-keys/:id).
// - Create: the LMS's POST /admin/lms/connections/:id/keys (the binding's
//   owner checks it), from Phase D's create form with read-only or
//   read-write in place of the roles. A person's own act: LMS admin (LMS
//   create and delete, the LMS's RequirePersonWith(PermAdmin)) and API_KEY
//   create, never while impersonating (the form isn't offered, and a note
//   says why). Create is off, saying why, while the connection is paused
//   (status, from the drawer's loaded connection) or the organization's API
//   access is off (the sidebar's read, useApiAccess, and its banner). The
//   secret is shown once in Phase D's dialog. Its expiry keeps a bound key's
//   own rules (BOUND_EXPIRY: 365 days by default, Never offered; core exempts
//   bound keys).
// - Rotate and revoke: content's routes, through Phase D's dialogs.
//
// Absent while the staff-only switch is closed: the server registers the
// create route only once it is lifted (lmsApiKeysVisible), and without
// API_KEY read. The drawer around it is the LMS admin's (staff-only while
// the switch is closed; the server checks the organization's feature_lms).

export const CONNECTION_KEY_ACCESS = [
  { id: 'read-only', name: 'Read-only', description: "Reads this connection: its courses, packages, LMS account links, sessions and activity." },
  { id: 'read-write', name: 'Read-write', description: 'Read-only, plus maps courses, builds packages and pauses the connection.' }
]

export const CONNECTION_PAUSED = 'The connection is paused; resume it first'

// A refused create in words: a paused connection (the LMS's binding check),
// else the LMS's own sentence (core's codes carried in its envelope), else a
// plain one (a proxy's bare status, the network).
export const createErrorText = err => {
  if (err && err.code === 'CONNECTION_NOT_ACTIVE') return CONNECTION_PAUSED
  if (err && err.readable) return err.message
  return "The key couldn't be created."
}

const INTRO = "The key can call the LMS for this connection only, with the access you give it. Its secret is shown once, when it's made."

// The switch is a code constant, read first: while it is closed (as shipped)
// the panel reads nothing at all.
export const ConnectionKeysPanel = ({ connectionId, status }) => (lmsApiKeysVisible() ? <GatedPanel connectionId={connectionId} status={status} /> : null)

const GatedPanel = ({ connectionId, status }) => {
  const writes = useKeyWrites()
  const lms = useRoleOrg(ROLE_ORG.LMS)
  const access = useApiAccess(writes.canRead)
  if (!writes.canRead) return null
  return (
    <Panel
      connectionId={connectionId}
      writes={writes}
      canCreate={writes.canCreate && !!lms.accessCreate && !!lms.accessDelete}
      paused={status === 'paused'}
      accessOff={!!access && access.enabled === false} />
  )
}

const Panel = ({ connectionId, writes, canCreate, paused, accessOff }) => {
  const headingId = useId()
  const tableRef = useRef()
  const [rows, setRows] = useState(undefined)
  const [error, failure, setError] = useErrorLine()
  const [creating, setCreating] = useState(false)
  const binding = useMemo(() => ({ kind: LMS_CONNECTION_BINDING, id: Number(connectionId) }), [connectionId])

  // Every key here is this connection's, so no row says "Bound to".
  const load = useCallback(async () => {
    try {
      const list = await listKeys(binding)
      setError(null)
      setRows(Array.isArray(list) ? list.filter(key => boundTo(key, binding)).map(key => ({ ...keyRow(key), bindingText: '' })) : [])
    } catch (err) {
      setRows(current => (Array.isArray(current) && current.length ? current : null))
      setError(serverMessage(err, "This connection's API keys couldn't be loaded."), err)
    }
  }, [binding, setError])

  const actions = useKeyActions({ onChanged: load })
  const menu = useKeyMenu(writes, actions)
  const gridList = useGridList(rows)

  const submit = useCallback(
    ({ name, description, choice, expiresAt }) => lmsAdmin.createConnectionKey(connectionId, { name, description, access: choice, expires_at: expiresAt }),
    [connectionId]
  )

  // The answer goes straight to the one-time dialog; nothing here keeps it.
  const created = answer => {
    setCreating(false)
    actions.reveal({ name: answer && answer.key ? answer.key.name : '', secret: answer && answer.secret })
    load()
  }

  // Offered to whoever may create one; off, saying why, while the
  // connection is paused or the organization's API access is off.
  const createOff = paused || accessOff
  const create = canCreate
    ? <button type="button" className="btn btn-sm sw-btn" disabled={createOff} onClick={() => setCreating(true)}><IconAdd className="me-1" />Create API key</button>
    : null
  const pausedNote = canCreate && paused
  const notes = writes.readOnlyReason || error || accessOff || pausedNote

  return (
    <section className="form-section" aria-labelledby={headingId} data-testid="connection-keys-section">
      <div className="form-section-title">
        <span className="num">
          <LmsIcon name="key" style={{ fontSize: 9 }} />
        </span>
        <h2 className="label" id={headingId}>API keys for this connection</h2>
      </div>
      <div className="field">
        <div className="field-help" style={{ marginTop: 0 }}>
          Keys your systems use to call the LMS for this connection only, read-only or read-write. While the connection
          is paused or archived its keys are refused. A key for every connection is made on{' '}
          <Link to="/org/api-keys">the organization's API keys page</Link>.
        </div>
      </div>
      {notes &&
        <div className="sw-workspace">
          {accessOff && <Notice tone="bad" role="alert">{ACCESS_OFF_BANNER}</Notice>}
          {pausedNote && <Notice tone="warn">{CONNECTION_PAUSED}</Notice>}
          {writes.readOnlyReason && <Notice tone="warn">{writes.readOnlyReason}</Notice>}
          {error && <Notice tone="bad" role="alert">{error}<ErrorReference error={failure} /></Notice>}
        </div>}
      <LmsGrid title="Keys" tableRef={tableRef} exportName="connection-api-keys" compact rowHeight={ROW_HEIGHT_TWO_LINE} actions={create}>
        <TableAgClient
          ref={tableRef}
          apiList={gridList}
          onReady={load}
          sortCol="createdText"
          sortDesc
          rowHeight={ROW_HEIGHT_TWO_LINE}
          emptyState={rows === null ? FAILED_STATE : {
            Icon: IconPassword,
            title: 'No API keys for this connection yet',
            message: canCreate ? 'Create one for each system that calls the LMS for this connection. Its secret is shown once, when it is made.' : 'Keys made for this connection show here.',
            ...(canCreate && !createOff ? { action: { label: 'Create API key', Icon: IconAdd, onClick: () => setCreating(true) } } : {})
          }}
          noMatchState={{ message: 'No keys match. Clear the search.' }}>
          <ColumnFormat field="name" header="Name" render={nameCell} exportValue={key => key.name} stacked flex={2} sortable />
          <ColumnText field="prefixText" header="Key" fit="content" sortable />
          <ColumnText field="roleName" header="Role" sortable />
          <ColumnFormat field="statusText" header="Status" render={statusCell} exportValue={key => key.statusText} fit="content" sortable />
          <ColumnFormat field="lastUsedText" header="Last used" render={lastUsedCell} comparator={byField('lastUsedMs')} exportValue={key => key.last_used_at || 'never'} fit="content" sortable />
          <ColumnFormat field="expiresText" header="Expires" render={expiresCell} comparator={byField('expiresMs')} exportValue={key => key.expires_at || key.expiresText} fit="content" sortable />
          <ColumnFormat field="createdText" header="Created" render={createdCell} comparator={byField('createdMs')} exportValue={key => key.created_at} fit="content" sortable hide />
          <ColumnMenu menu={menu} />
        </TableAgClient>
      </LmsGrid>

      {creating &&
        <ApiKeyCreateDrawer
          open
          title="Create an API key for this connection"
          intro={INTRO}
          choices={CONNECTION_KEY_ACCESS}
          choiceLabel="Access"
          submit={submit}
          errorText={createErrorText}
          expiry={BOUND_EXPIRY}
          onClose={() => setCreating(false)}
          onCreated={created} />}
      {actions.dialogs}
    </section>
  )
}
